import { createRouter, createWebHistory } from 'vue-router'
import type { RouteLocationNormalized, RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth.store'
import { useAuthPermissionsStore } from '@/stores/auth-permissions.store'
import { AdminPermission } from '@/services/administrator-permissions'
import { scopeFromSegments } from '@/services/evaluations/run-scope'
import { isProduction, setDocumentTitle } from '../utils/environmentUtils'
import Login from '../views/Login.vue'
import AuthCallback from '../views/AuthCallback.vue'
import Layout from '../views/Layout.vue'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: '/admin/dashboard'
  },
  {
    path: '/admin/login',
    name: 'login',
    component: Login,
    meta: { guest: true }
  },
  {
    path: '/adx/auth/callback',
    name: 'auth-callback',
    component: AuthCallback,
    meta: { guest: true }
  },
  {
    path: '/admin',
    component: Layout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        redirect: '/admin/dashboard'
      },
      {
        path: 'dashboard',
        name: 'dashboard',
        component: () => import('../views/Dashboard.vue')
      },
      {
        path: 'administrators',
        name: 'administrators',
        component: () => import('../views/administrators/AdministratorsList.vue')
      },
      {
        path: 'permissions',
        name: 'permissions',
        component: () => import('../views/permissions/PermissionsIndex.vue'),
        meta: { requiresPermission: AdminPermission.AdministratorsView }
      },
      {
        path: 'merchants',
        name: 'merchants',
        component: () => import('../views/merchants/MerchantsList.vue')
      },
      {
        path: 'merchants/:id',
        component: () => import('../views/merchants/MerchantDetail.vue'),
        props: true,
        children: [
          { path: '', redirect: { name: 'merchant-overview' } },
          {
            path: 'overview',
            name: 'merchant-overview',
            component: () => import('../views/merchants/merchant-detail/OverviewSection.vue')
          },
          {
            path: 'integrations',
            name: 'merchant-integrations',
            component: () => import('../views/merchants/merchant-detail/IntegrationsSection.vue')
          },
          {
            path: 'integrations/:integration',
            name: 'merchant-integration-detail',
            component: () =>
              import('../views/merchants/merchant-detail/integrations/IntegrationDetail.vue'),
            props: true
          },
          {
            path: 'users',
            name: 'merchant-users',
            component: () => import('../views/merchants/merchant-detail/UsersSection.vue')
          },
          {
            path: 'locations',
            name: 'merchant-locations',
            component: () => import('../views/merchants/merchant-detail/LocationsSection.vue')
          },
          {
            path: 'groups',
            name: 'merchant-groups',
            component: () => import('../views/merchants/merchant-detail/GroupsSection.vue')
          },
          {
            path: 'templates',
            name: 'merchant-templates',
            component: () => import('../views/merchants/merchant-detail/TemplatesSection.vue')
          },
          {
            path: 'email',
            name: 'merchant-email',
            component: () => import('../views/merchants/merchant-detail/EmailSection.vue')
          },
          {
            path: 'questionnaires',
            name: 'merchant-questionnaires',
            component: () =>
              import('../views/merchants/merchant-detail/QuestionnairesWrapperSection.vue')
          },
          {
            path: 'rewards',
            name: 'merchant-rewards',
            component: () => import('../views/merchants/merchant-detail/RewardsSection.vue')
          },
          {
            path: 'sms',
            name: 'merchant-sms',
            component: () => import('../views/merchants/merchant-detail/SmsSection.vue')
          },
          {
            path: 'items',
            name: 'merchant-items',
            component: () => import('../views/merchants/merchant-detail/ItemsSection.vue')
          },
          {
            path: 'settings',
            name: 'merchant-settings',
            component: () => import('../views/merchants/merchant-detail/SettingsSection.vue')
          },
          {
            path: 'ai-coach',
            name: 'merchant-ai-coach',
            component: () => import('../views/merchants/merchant-detail/AiCoachSection.vue')
          },
          {
            path: 'objectives',
            name: 'merchant-objectives',
            component: () => import('../views/merchants/merchant-detail/ObjectivesSection.vue')
          },
          {
            path: 'preferences',
            name: 'merchant-preferences',
            component: () => import('../views/merchants/merchant-detail/PreferencesSection.vue')
          },
          {
            path: 'toolbox',
            name: 'merchant-toolbox',
            component: () => import('../views/merchants/merchant-detail/ToolboxSection.vue')
          },
          {
            path: 'bulk-upload/locations',
            name: 'merchant-bulk-upload-locations',
            component: () => import('../views/bulk-upload/LocationsBulkUpload.vue')
          },
          {
            path: 'bulk-upload/groups',
            name: 'merchant-bulk-upload-groups',
            component: () => import('../views/bulk-upload/GroupsBulkUpload.vue')
          },
          {
            path: 'bulk-upload/users',
            name: 'merchant-bulk-upload-users',
            component: () => import('../views/bulk-upload/UsersBulkUpload.vue')
          },
          {
            path: 'webhooks',
            name: 'merchant-webhooks',
            component: () =>
              import('../views/merchants/merchant-detail/WebhooksSection.vue')
          }
        ]
      },
      {
        path: 'merchant-types',
        name: 'merchant-types',
        component: () => import('../views/merchant-types/MerchantTypesList.vue')
      },
      {
        path: 'partners',
        name: 'partners',
        component: () => import('../views/partners/PartnersList.vue')
      },
      {
        path: 'partner-integrators',
        name: 'partner-integrators',
        component: () => import('../views/partner-integrators/PartnerIntegrators.vue')
      },
      {
        path: 'partner-integrators/:partnerRef',
        name: 'partner-integrator-detail',
        component: () => import('../views/partner-integrators/PartnerIntegratorDetail.vue'),
        props: true
      },
      {
        path: 'orders',
        name: 'orders',
        component: () => import('../views/orders/OrdersList.vue')
      },
      {
        path: 'marketing-images',
        name: 'marketing-images',
        component: () => import('../views/marketing-images/MarketingImagesList.vue')
      },
      {
        path: 'preferences',
        name: 'preferences',
        component: () => import('../views/preferences/PreferencesList.vue')
      },
      {
        path: 'preferences/:label',
        name: 'preference-detail',
        component: () => import('../views/preferences/PreferenceDetail.vue'),
        props: true
      },
      {
        path: 'message-template-defaults',
        name: 'message-template-defaults',
        component: () =>
          import('../views/message-template-defaults/MessageTemplateDefaultsList.vue')
      },
      {
        path: 'metabase',
        name: 'metabase',
        component: () => import('../views/metabase/MetabaseDashboardList.vue'),
        meta: { requiresPermission: AdminPermission.MetabaseDashboardsView }
      },
      {
        path: 'metabase/:id/edit',
        name: 'metabase-edit',
        component: () => import('../views/metabase/MetabaseDashboardEdit.vue'),
        meta: { requiresPermission: AdminPermission.MetabaseDashboardsEdit }
      },
      {
        path: 'reports',
        name: 'reports',
        component: () => import('../views/reports/ReportsPage.vue')
      },
      {
        path: 'reports/collection/:collectionId',
        name: 'reports-collection',
        component: () => import('../views/reports/ReportsPage.vue'),
        props: true
      },
      {
        path: 'audit-logs',
        name: 'audit-logs',
        component: () => import('../views/audit-logs/AuditLogsList.vue')
      },
      {
        path: 'automation-runs',
        name: 'automation-runs',
        component: () => import('../views/automation-runs/AutomationRunsList.vue')
      },
      {
        path: 'automation-runs/:automationId',
        name: 'automation-run-detail',
        component: () => import('../views/automation-runs/AutomationRunDetail.vue'),
        props: true
      },
      {
        path: 'data-subject-requests',
        name: 'data-subject-requests',
        component: () => import('../views/data-subject-requests/DataSubjectRequestsList.vue'),
        meta: { requiresPermission: AdminPermission.DataSubjectRequestsView }
      },
      {
        path: 'social-media/review-audit',
        name: 'social-media-review-audit',
        component: () =>
          import(
            '../views/social-media/review-audit/ReviewAuditList.vue'
          )
      },
      {
        path: 'social-media/chatmeter-locations',
        name: 'social-media-chatmeter-locations',
        component: () =>
          import(
            '../views/social-media/chatmeter-locations/ChatmeterLocations.vue'
          ),
        meta: { requiresPermission: AdminPermission.SocialMediaChatmeterLocationsView }
      },
      {
        path: 'social-media/review-audit/:merchantId',
        name: 'social-media-review-audit-detail',
        component: () =>
          import(
            '../views/social-media/review-audit/ReviewAuditDetail.vue'
          ),
        props: true
      },
      {
        path: 'user-logs',
        name: 'user-logs',
        component: () => import('../views/user-logs/UserLogsList.vue')
      },
      {
        path: 'webhook-logs',
        name: 'webhook-logs',
        component: () => import('../views/webhook-logs/WebhookLogsList.vue')
      },
      {
        path: 'data-replication',
        name: 'data-replication',
        component: () => import('../views/data-replication/DataReplicationDashboard.vue'),
        meta: { requiresPermission: AdminPermission.DataReplicationView }
      },
      {
        path: 'billing/sms',
        redirect: () => {
          const now = new Date()
          const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
          return { name: 'sms-billing', params: { month } }
        }
      },
      {
        path: 'billing/sms/:month',
        name: 'sms-billing',
        component: () => import('../views/billing/SmsBilling.vue'),
        props: true,
        meta: { requiresPermission: AdminPermission.SmsBillingView }
      },
      {
        path: 'billing/sms/:month/merchant/:merchantId',
        name: 'sms-billing-detail',
        component: () => import('../views/billing/SmsBillingDetail.vue'),
        props: true,
        meta: { requiresPermission: AdminPermission.SmsBillingView }
      },
      {
        path: 'billing/merchant-fees',
        name: 'merchant-fees',
        component: () => import('../views/billing/merchant-fees/MerchantFeesList.vue'),
        meta: { requiresPermission: AdminPermission.BillingRatesView }
      },
      {
        path: 'billing/merchant-fees/:rateName',
        name: 'merchant-fee-detail',
        component: () => import('../views/billing/merchant-fees/MerchantFeeDetail.vue'),
        props: true,
        meta: { requiresPermission: AdminPermission.BillingRatesView }
      },
      {
        path: 'email',
        name: 'email-admin',
        component: () => import('../views/email/EmailAdmin.vue')
      },
      {
        path: 'feedback-requests',
        name: 'feedback-request-cycles',
        component: () => import('../views/feedback-requests/FeedbackRequestCyclesList.vue'),
        meta: { requiresPermission: AdminPermission.EmailMessagesView }
      },
      {
        path: 'feedback-requests/:cycleId',
        name: 'feedback-request-cycle-detail',
        component: () => import('../views/feedback-requests/FeedbackRequestCycleDetail.vue'),
        props: true,
        meta: { requiresPermission: AdminPermission.EmailMessagesView }
      },
      // Test-inbox tooling — the Email/SMS inbox viewers and their analytics
      // dashboards — is backed by the in-house message sink, which only runs
      // in dev/staging. The side-nav and merchant kebab links are already
      // prod-gated; disallowOnProd closes the direct-URL hole so the
      // beforeEach guard redirects these to /admin/dashboard on prod.
      {
        // Unified Test Inbox — one merged email + SMS timeline, and the only
        // inbox URL. The former /admin/email-testing and /admin/sms-testing
        // pages are gone; everything (list, message detail, analytics) lives
        // under /admin/test-inbox/*.
        path: 'test-inbox',
        name: 'test-inbox',
        component: () => import('../views/test-inbox/InboxList.vue'),
        meta: { disallowOnProd: true }
      },
      {
        // Email message detail. Component still lives in views/test-inbox/
        // (internal folder name), but the URL is under /admin/test-inbox.
        path: 'test-inbox/email/:id',
        name: 'test-inbox-email-detail',
        component: () => import('../views/test-inbox/InboxDetail.vue'),
        props: true,
        meta: { disallowOnProd: true }
      },
      {
        // SMS message detail.
        path: 'test-inbox/sms/:id',
        name: 'test-inbox-sms-detail',
        component: () => import('../views/test-inbox/SmsDetail.vue'),
        props: true,
        meta: { disallowOnProd: true }
      },
      {
        path: 'test-inbox/journey',
        name: 'test-inbox-journey',
        component: () => import('../views/test-inbox/CustomerJourney.vue'),
        meta: { disallowOnProd: true }
      },
      {
        path: 'test-inbox/coverage',
        name: 'test-inbox-coverage',
        component: () => import('../views/test-inbox/TemplateCoverage.vue'),
        meta: { disallowOnProd: true }
      },
      {
        path: 'test-inbox/usage',
        name: 'test-inbox-usage',
        component: () => import('../views/test-inbox/UsageDashboard.vue'),
        meta: { disallowOnProd: true }
      },
      {
        path: 'sms-blacklist',
        name: 'sms-blacklist',
        component: () => import('../views/sms-blacklist/SmsBlacklist.vue'),
        meta: { requiresPermission: AdminPermission.SmsBlacklistManage }
      },
      {
        path: 'social-media-reply-error-config',
        name: 'social-media-reply-error-config',
        component: () =>
          import('../views/social-media/error-config/ReplyErrorConfig.vue'),
        meta: { requiresPermission: AdminPermission.SocialMediaReplyErrorConfigManage }
      },
      {
        path: 'evaluations',
        name: 'evaluations',
        component: () => import('../views/evaluations/EvaluationsList.vue'),
        meta: { requiresPermission: AdminPermission.EvaluationsView, disallowOnProd: true }
      },
      // An eval result's URL mirrors its S3 key, so the run scope is path segments rather than a
      // query parameter — repeatable because a laptop scope is `email=…/branch=…` and vue-router
      // encodes a `/` inside a single param as %2F. See run-scope.ts.
      //
      // Declared most-specific first, and the execution segment is constrained to the run's
      // `yyyyMMddTHHmmss` name so it cannot instead be read as a second scope segment.
      {
        path: 'evaluations/:feature/:scope+/:executionName(\\d{8}T\\d{6})',
        name: 'evaluation-execution',
        component: () => import('../views/evaluations/ExecutionDetail.vue'),
        props: route => ({
          feature: route.params.feature,
          scope: scopeFromSegments(route.params.scope),
          executionName: route.params.executionName
        }),
        meta: { requiresPermission: AdminPermission.EvaluationsView, disallowOnProd: true }
      },
      // `:scope*` is zero-or-more, so this one route also serves the scope-less URL the features
      // list links to — it cannot know a scope. The page resolves the default and replaces itself
      // with the scoped URL.
      {
        path: 'evaluations/:feature/:scope*',
        name: 'evaluation-feature',
        component: () => import('../views/evaluations/FeatureExecutions.vue'),
        props: route => ({
          feature: route.params.feature,
          scope: scopeFromSegments(route.params.scope)
        }),
        meta: { requiresPermission: AdminPermission.EvaluationsView, disallowOnProd: true }
      },
      {
        path: 'test-executions',
        name: 'test-executions',
        component: () => import('../views/test-executions/TestExecutionsList.vue'),
        meta: { requiresPermission: AdminPermission.TestExecutionsView, disallowOnProd: true }
      },
      {
        path: 'test-executions/:testType/:executionId',
        name: 'test-execution-detail',
        component: () => import('../views/test-executions/TestExecutionDetail.vue'),
        props: true,
        meta: { requiresPermission: AdminPermission.TestExecutionsView, disallowOnProd: true }
      },
      {
        path: 'social-media/resync',
        name: 'social-media-resync',
        component: () => import('../views/social-media/resync/SocialMediaResync.vue')
      },
      {
        path: 'tools/send-orders',
        name: 'send-orders',
        component: () => import('../views/developer-tools/SendOrders.vue'),
        // Send Orders has no hideInProduction flag in Layout.vue — visible in prod —
        // so it gets a permission gate but no disallowOnProd.
        meta: { requiresPermission: AdminPermission.SendOrders }
      },
      {
        path: 'tools/send-events',
        name: 'send-events',
        component: () => import('../views/developer-tools/SendEvents.vue'),
        meta: { requiresPermission: AdminPermission.SendEvents, disallowOnProd: true }
      },
      {
        path: 'tools/add-merchant',
        name: 'add-merchant',
        component: () => import('../views/developer-tools/AddMerchant.vue'),
        meta: { requiresPermission: AdminPermission.AddMerchant, disallowOnProd: true }
      },
      {
        path: 'tools/add-surveys',
        name: 'add-surveys',
        component: () => import('../views/developer-tools/AddSurveys.vue'),
        meta: { requiresPermission: AdminPermission.AddTestData, disallowOnProd: true }
      },
      {
        path: 'tools/add-reviews',
        name: 'add-reviews',
        component: () => import('../views/developer-tools/AddReviews.vue'),
        meta: { requiresPermission: AdminPermission.AddTestData, disallowOnProd: true }
      },
      {
        path: 'tools/add-forms',
        name: 'add-forms',
        component: () => import('../views/developer-tools/AddForms.vue'),
        meta: { requiresPermission: AdminPermission.AddTestData, disallowOnProd: true }
      },
      {
        path: 'tools/cleanup-merchant',
        name: 'cleanup-merchant',
        component: () => import('../views/developer-tools/CleanupMerchant.vue'),
        meta: { requiresPermission: AdminPermission.CleanupMerchant, disallowOnProd: true }
      },
      {
        path: 'tools/run-tests',
        name: 'run-tests',
        component: () => import('../views/developer-tools/RunTests.vue'),
        meta: { requiresPermission: AdminPermission.RunTests, disallowOnProd: true }
      },
      {
        path: 'tools/partner-console',
        name: 'partner-console',
        component: () => import('../views/developer-tools/PartnerConsole.vue'),
        // Reachable in production, unlike its dev-tools neighbours, because it injects no test
        // data: picking fewer scopes narrows the minted impersonation token and leaves the
        // partner's own scopes alone (TE-13396). PartnerImpersonate is the whole gate.
        meta: { requiresPermission: AdminPermission.PartnerImpersonate }
      },
      {
        path: 'tools/my-work',
        name: 'my-work',
        component: () => import('../views/my-work/MyWork.vue')
        // No ADX permission gate and no disallowOnProd: the fleet works out the person from their
        // own Cognito token on every call, and shows each engineer only their own tickets and PRs.
      },
      {
        path: 'tools/releases',
        name: 'release-view',
        component: () => import('../views/releases/ReleaseView.vue')
        // No ADX permission gate and no disallowOnProd, like My work: the fleet works out the person
        // from their own Cognito token, and starts actions only on their own tickets.
      },
      {
        path: 'tools/pull-requests',
        name: 'claude-repo-prs',
        component: () => import('../views/claude-repo-prs/RepoPrs.vue'),
        // No ADX permission gate and no disallowOnProd, like My work: the fleet checks the caller's own Cognito
        // token, and each row's actions go through the fleet, which decides who may run what.
        meta: { title: 'Pull requests' }
      },
      {
        path: 'tools/qa-queue',
        name: 'qa-queue',
        component: () => import('../views/qa-queue/QaQueue.vue')
        // No ADX permission gate and no disallowOnProd, like the Releases page: the fleet works out the
        // person from their own Cognito token, and starts tests only on their own tickets (or an operator's).
      },
      {
        path: 'tools/qwe-scoreboard',
        name: 'claude-qwe-scoreboard',
        component: () => import('../views/qwe-scoreboard/QweScoreboard.vue'),
        // No ADX permission gate and no disallowOnProd: the fleet does its own staff check, reads Jira as the
        // caller, and files a draft ticket only through the caller's own Jira connection.
        meta: { title: 'QWE scoreboard' }
      },
      {
        path: 'tools/tickets/:key',
        name: 'claude-ticket',
        component: () => import('../views/tickets/TicketPage.vue')
        // No ADX permission gate and no disallowOnProd, like the Releases page: the fleet does its own
        // staff check on the caller's token and hides names from the Playwright test reader.
      },
      {
        path: 'tools/needs-attention',
        name: 'claude-needs-attention',
        component: () => import('../views/claude-team/ClaudeNeedsAttention.vue')
        // No ADX permission gate and no disallowOnProd, like the Releases page: the fleet does its own
        // staff check on the caller's token and presses each card button as the caller's own Slack user.
      },
      {
        path: 'tools/oversight',
        name: 'claude-oversight',
        component: () => import('../views/claude-team/ClaudeOversight.vue')
        // No ADX permission gate and no disallowOnProd, as Needs attention.
      },
      {
        path: 'tools/tests',
        name: 'claude-tests',
        component: () => import('../views/claude-quality/ClaudeQuality.vue')
        // No ADX permission gate and no disallowOnProd: the fleet does its own staff check, and refuses the one
        // write (a sweep question's answer) to the Playwright test reader.
      },
      {
        path: 'tools/metrics',
        name: 'claude-metrics',
        component: () => import('../views/claude-metrics/ClaudeMetrics.vue')
        // No ADX permission gate and no disallowOnProd: read-only, and the fleet does its own staff check.
      },
      {
        path: 'tools/health',
        name: 'claude-health',
        component: () => import('../views/claude-health/ClaudeHealth.vue'),
        // No ADX permission gate and no disallowOnProd, as Tests and Metrics: read-only, and the fleet does its own
        // staff check.
        meta: { title: 'Health' }
      },
      {
        path: 'tools/squads',
        name: 'claude-squads',
        component: () => import('../views/claude-squads/ClaudeSquads.vue'),
        // No ADX permission gate and no disallowOnProd: read-only, and the fleet does its own staff check.
        meta: { title: 'Squads' }
      },
      {
        path: 'tools/backlog',
        name: 'claude-backlog',
        component: () => import('../views/claude-backlog/ClaudeBacklog.vue'),
        // No ADX permission gate and no disallowOnProd: the fleet does its own staff check, and decides on every
        // Promote and Dismiss whether this person may act.
        meta: { title: 'Backlog' }
      },
      {
        path: 'tools/pipelines',
        name: 'claude-pipelines',
        component: () => import('../views/claude-pipelines/ClaudePipelines.vue')
        // No ADX permission gate and no disallowOnProd, as the Fleet page it moved out of.
      },
      {
        path: 'tools/claude-tools',
        name: 'claude-tools',
        component: () => import('../views/claude-tools/ClaudeTools.vue')
        // No ADX permission gate and no disallowOnProd: read-only, and the fleet does its own staff check.
      },
      {
        path: 'tools/team-settings',
        name: 'claude-team-settings',
        component: () => import('../views/claude-team-settings/TeamSettings.vue'),
        // No ADX permission gate and no disallowOnProd: read-only, and the fleet does its own staff check.
        meta: { title: 'Team settings' }
      },
      {
        path: 'tools/wishlist',
        name: 'claude-wishlist',
        component: () => import('../views/claude-wishlist/ClaudeWishlist.vue'),
        // No ADX permission gate and no disallowOnProd: the fleet does its own staff and operator checks on every call.
        meta: { title: 'Wishlist' }
      },
      {
        path: 'tools/claude-fleet',
        name: 'claude-fleet',
        component: () => import('../views/claude-fleet/ClaudeFleet.vue'),
        // No ADX permission gate and no disallowOnProd: the fleet service verifies the admin's own
        // Cognito token and admin group on every call, and the fleet is one fleet across environments.
        // It sits under Claude in the sidebar, so its title drops the word its route name keeps.
        meta: { title: 'Fleet' }
      },
      {
        path: 'tools/generate-receipt',
        name: 'generate-receipt',
        component: () => import('../views/developer-tools/GenerateReceipt.vue')
        // No permission gate and no disallowOnProd. The page renders client-side and writes
        // nothing; the order data it reads is gated on OrdersView at the API.
      },
      {
        path: 'tools/generate-qr-code',
        name: 'generate-qr-code',
        component: () => import('../views/developer-tools/GenerateQrCode.vue')
        // No permission gate and no disallowOnProd. Saving a style is the gated action
        // (QrStylesManage); opening the page and generating a QR is not.
      },
      {
        path: 'mobile-simulator',
        name: 'mobile-simulator',
        component: () => import('../views/mobile-simulator/MobileSimulator.vue')
      },
      {
        path: 'logout',
        name: 'logout',
        beforeEnter: (to, from, next) => {
          const authStore = useAuthStore()
          authStore.logout()
          next('/admin/login')
        }
      }
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/admin'
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Navigation guard for authentication + per-route permission/env gates
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  // Ensure auth state is loaded from localStorage before checking
  // This prevents race conditions on first navigation
  await authStore.checkAuth()

  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    return next('/admin/login')
  }

  if (to.meta.guest && authStore.isAuthenticated && to.name !== 'login') {
    // Login route lets authenticated users through so they can choose between
    // continuing as the current user or signing in as a different one.
    return next('/admin/dashboard')
  }

  // Routes flagged disallowOnProd are non-production tooling (e.g. dev-tools
  // pages that inject test data). Block them outright on prod regardless of
  // permission so a stray URL can't trigger test-data writes against real
  // merchant data.
  if (to.meta.disallowOnProd && isProduction()) {
    return next('/admin/dashboard')
  }

  // Routes flagged with requiresPermission need a specific AdminPermission
  // beyond plain authentication. checkAuth() above already populates the
  // permissions store, so it's safe to read synchronously here.
  if (to.meta.requiresPermission) {
    const permissionsStore = useAuthPermissionsStore()
    const required = to.meta.requiresPermission as AdminPermission
    if (!permissionsStore.hasPermission(required)) {
      return next('/admin/dashboard')
    }
  }

  return next()
})

/** A page's `meta.title`, else its route name in words ("merchant-types" → "Merchant types"). */
function pageTitle(to: RouteLocationNormalized): string {
  if (typeof to.meta.title === 'string') return to.meta.title
  const routeName = to.name?.toString() || ''
  return routeName ? `${routeName.charAt(0).toUpperCase()}${routeName.slice(1).replace(/-/g, ' ')}` : ''
}

// Set document title with environment prefix
router.afterEach(to => {
  const title = pageTitle(to)
  setDocumentTitle(title ? `${title} - ADX` : 'ADX Admin')
})

// Metrics → Usage counts which Claude pages people open. Loaded on demand, off the navigation's path.
router.afterEach(to => {
  if (!import.meta.env.VITE_FLEET_API_BASE_URL || !to.path.startsWith('/admin/tools/')) return
  import('@/services/claude-usage')
    .then(usage => usage.countClaudeView(to))
    .catch(() => undefined)
})

// Handle stale chunk errors after deployment — when a user's browser has
// cached an old index.html referencing chunk hashes that no longer exist,
// force a full page reload to fetch the latest assets.
router.onError((error, to) => {
  if (
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Importing a module script failed')
  ) {
    window.location.href = to.fullPath
  }
})

export default router
